<template>
    <div class="audio-library-picker">
        <!-- Selected file display -->
        <ion-item v-if="selectedAudio" lines="full" mode="md">
            <div class="file-label">
                <span class="audio-name">{{ selectedAudio.name }}</span>
                <span v-if="hasKnownMediaSizeKb(selectedAudio.sizeKb)" class="audio-size">
                    {{ formatSizeKb(selectedAudio.sizeKb) }}
                </span>
                <div class="icon-container" @click="clearSelection">
                    <ion-icon :icon="closeOutline"></ion-icon>
                </div>
            </div>
        </ion-item>

        <!-- Picker trigger -->
        <ion-item v-else lines="none" mode="md">
            <div class="button-with-label">
                <button type="button" class="custom-button button-dark file-button" @click="openLibrary">
                    <ion-icon class="button-icon" :icon="musicalNote"></ion-icon>
                    <span>{{ label }}</span>
                </button>
            </div>
        </ion-item>

        <!-- Library modal -->
        <div v-if="libraryOpen" class="library-overlay">
            <div class="library-modal">
                <div class="library-header">
                    <h3>Audio Library</h3>
                    <button type="button" class="close-btn" @click="closeLibrary">
                        <ion-icon :icon="closeOutline"></ion-icon>
                    </button>
                </div>

                <div class="library-body">
                    <!-- Search and sort controls -->
                    <div v-if="!loading && audioFiles.length > 0" class="library-controls">
                        <input
                            v-model="searchQuery"
                            type="search"
                            class="search-input"
                            placeholder="Search files…" />
                        <select v-model="sortBy" class="sort-select">
                            <option value="date-desc">Newest first</option>
                            <option value="date-asc">Oldest first</option>
                            <option value="name-asc">Name A–Z</option>
                            <option value="name-desc">Name Z–A</option>
                            <option value="size-desc">Largest first</option>
                            <option value="size-asc">Smallest first</option>
                        </select>
                    </div>

                    <!-- Loading -->
                    <div v-if="loading" class="library-loading">
                        <ion-spinner name="crescent"></ion-spinner>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="audioFiles.length === 0" class="library-empty">
                        No audio files uploaded yet.
                    </div>

                    <!-- File list -->
                    <div v-else class="audio-list">
                        <div v-if="filteredAudioFiles.length === 0" class="library-empty">
                            No files match your search.
                        </div>
                        <div
                            v-for="file of filteredAudioFiles"
                            :key="file.id"
                            class="audio-item"
                            :class="{ 'audio-item--selected': selectedAudio && selectedAudio.id === file.id }"
                            @click="selectFile(file)">
                            <div class="audio-item-info">
                                <span class="audio-item-name">{{ file.name }}</span>
                                <span class="audio-item-date">{{ formatDate(file.createdDate) }}</span>
                                <span v-if="hasKnownMediaSizeKb(file.sizeKb)" class="audio-item-size">{{ formatSizeKb(file.sizeKb) }}</span>
                                <span v-else-if="file.processingMsg" class="audio-item-status audio-item-status--error">
                                    {{ file.processingMsg }}
                                </span>
                            </div>
                            <div class="audio-item-actions" @click.stop>
                                <button
                                    v-if="isLibraryAudioReady(file)"
                                    type="button"
                                    class="play-btn"
                                    :class="{ 'play-btn--playing': playingId === file.id }"
                                    @click="togglePlay(file)">
                                    <ion-icon :icon="playingId === file.id ? pauseOutline : playOutline"></ion-icon>
                                </button>
                                <div v-else-if="!file.processingMsg" class="processing-spinner">
                                    <ion-spinner name="crescent"></ion-spinner>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Upload new file -->
                    <div class="upload-section">
                        <label class="upload-label">
                            <ion-icon :icon="cloudUploadOutline"></ion-icon>
                            <span>Upload new file</span>
                            <input
                                type="file"
                                @change="handleUpload"
                                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"/>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onUnmounted } from 'vue';
import {
    IonItem,
    IonIcon,
    IonSpinner,
    loadingController,
} from '@ionic/vue';
import { useStore } from 'vuex';
import {
    closeOutline,
    musicalNote,
    playOutline,
    pauseOutline,
    cloudUploadOutline,
} from 'ionicons/icons';
import { GET, FILEUPLOAD } from '@/services/HTTP';
import { presentNativeAlert } from '@/services/Notify';
import { formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady } from '@/utils/mediaSizeDisplay';
import { waitUntilAudioReadyToPlay } from '@/utils/audioBlobPlayback';

interface AudioFile {
    id: string;
    name: string;
    createdDate: string;
    sizeKb: number | null;
    processingMsg: string | null;
    instanceId: string | null;
    audioGroupId: string;
}

export default defineComponent({
    name: 'AudioLibraryPicker',
    components: { IonItem, IonIcon, IonSpinner },
    props: {
        audioId: {
            type: String,
            default: null,
        },
        audioName: {
            type: String,
            default: '',
        },
        audioSizeKb: {
            type: Number,
            default: null,
        },
        label: {
            type: String,
            default: 'Choose Audio',
        },
        audioGroupId: {
            type: String,
            required: true,
        },
    },
    emits: ['update:audioId', 'update:audioName', 'update:audioSizeKb'],
    setup(props, ctx) {
        const store = useStore();
        const instanceId = computed(() => store.getters['app/selectedAppInstanceId']);

        const libraryOpen = ref(false);
        const loading = ref(false);
        const audioFiles = ref<AudioFile[]>([]);
        const playingId = ref<string | null>(null);
        const currentAudio = ref<HTMLAudioElement | null>(null);
        const searchQuery = ref('');
        const sortBy = ref('date-desc');
        const pollingIds = ref(new Set<string>());

        const pollUntilReady = (id: string) => {
            if (pollingIds.value.has(id)) return;
            pollingIds.value.add(id);

            const interval = setInterval(async () => {
                // Stop polling if modal closed or file no longer in list
                const idx = audioFiles.value.findIndex(f => f.id === id);
                if (!libraryOpen.value || idx === -1) {
                    clearInterval(interval);
                    pollingIds.value.delete(id);
                    return;
                }
                try {
                    const updated: AudioFile = await GET(`/audio/${id}`, false);
                    if (updated && (hasKnownMediaSizeKb(updated.sizeKb) || updated.processingMsg)) {
                        audioFiles.value[idx] = { ...audioFiles.value[idx], ...updated };
                        clearInterval(interval);
                        pollingIds.value.delete(id);
                    }
                } catch {
                    clearInterval(interval);
                    pollingIds.value.delete(id);
                }
            }, 3000);
        };

        const filteredAudioFiles = computed(() => {
            let list = audioFiles.value;
            const q = searchQuery.value.trim().toLowerCase();
            if (q) {
                list = list.filter(f => f.name.toLowerCase().includes(q));
            }
            return [...list].sort((a, b) => {
                switch (sortBy.value) {
                    case 'date-asc':  return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                    case 'name-asc':  return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'size-desc': return (b.sizeKb ?? -1) - (a.sizeKb ?? -1);
                    case 'size-asc':  return (a.sizeKb ?? Infinity) - (b.sizeKb ?? Infinity);
                    default:          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                }
            });
        });

        const selectedAudio = computed<AudioFile | null>(() => {
            // Show selected state for library reference (audioId) or legacy blob (audioName only)
            if (!props.audioId && !props.audioName) return null;
            return {
                id: props.audioId || 'legacy',
                name: props.audioName || '',
                sizeKb: props.audioSizeKb,
                processingMsg: null,
                createdDate: '',
                instanceId: instanceId.value,
                audioGroupId: props.audioGroupId,
            };
        });

        const openLibrary = async () => {
            libraryOpen.value = true;
            loading.value = true;
            try {
                audioFiles.value = await GET(`/audio/instance/${instanceId.value}`, false) ?? [];
                // Resume polling for any files still processing
                audioFiles.value.filter(f => !hasKnownMediaSizeKb(f.sizeKb) && !f.processingMsg).forEach(f => pollUntilReady(f.id));
            } finally {
                loading.value = false;
            }
        };

        const stopAudio = () => {
            if (currentAudio.value) {
                currentAudio.value.pause();
                if (currentAudio.value.src?.startsWith('blob:')) {
                    URL.revokeObjectURL(currentAudio.value.src);
                }
                currentAudio.value = null;
            }
            playingId.value = null;
        };

        const closeLibrary = () => {
            libraryOpen.value = false;
            stopAudio();
        };

        const clearSelection = () => {
            ctx.emit('update:audioId', null);
            ctx.emit('update:audioName', '');
            ctx.emit('update:audioSizeKb', null);
        };

        const selectFile = (file: AudioFile) => {
            ctx.emit('update:audioId', file.id);
            ctx.emit('update:audioName', file.name);
            ctx.emit('update:audioSizeKb', file.sizeKb);
            closeLibrary();
        };

        const handleUpload = async (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];

            const loader = await loadingController.create({ message: 'Uploading…' });
            await loader.present();
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('audioGroupId', props.audioGroupId);
                if (instanceId.value) formData.append('instanceId', instanceId.value);

                const created: AudioFile = await FILEUPLOAD('/audio/upload', formData);
                if (created) {
                    audioFiles.value.unshift(created);
                    // Start polling immediately — processing begins right after upload
                    pollUntilReady(created.id);
                }
            } finally {
                await loader.dismiss();
                input.value = '';
            }
        };

        const togglePlay = async (file: AudioFile) => {
            if (playingId.value === file.id) {
                stopAudio();
                return;
            }
            stopAudio();

            playingId.value = file.id;
            try {
                const result = await GET(`/audio/${file.id}/stream`, true);
                if (playingId.value !== file.id) {
                    return;
                }
                if (!result.blob.size) {
                    playingId.value = null;
                    await presentNativeAlert({
                        cssClass: 'custom-alert',
                        header: 'Playback failed',
                        message: 'The audio file is empty or still processing.',
                        buttons: ['OK'],
                    });
                    return;
                }
                const objectUrl = URL.createObjectURL(result.blob);
                const audio = new Audio();
                audio.preload = 'auto';
                audio.src = objectUrl;
                audio.onended = () => {
                    playingId.value = null;
                    currentAudio.value = null;
                    URL.revokeObjectURL(objectUrl);
                };
                currentAudio.value = audio;
                await waitUntilAudioReadyToPlay(audio);
                await audio.play();
            } catch {
                stopAudio();
                await presentNativeAlert({
                    cssClass: 'custom-alert',
                    header: 'Playback failed',
                    message: 'Could not play this file. If it was just uploaded, wait for processing to finish.',
                    buttons: ['OK'],
                });
            }
        };

        const formatDate = (iso: string) => {
            if (!iso) return '';
            return new Date(iso).toLocaleDateString();
        };

        onUnmounted(() => stopAudio());

        return {
            libraryOpen,
            loading,
            audioFiles,
            filteredAudioFiles,
            searchQuery,
            sortBy,
            playingId,
            selectedAudio,
            openLibrary,
            closeLibrary,
            clearSelection,
            selectFile,
            handleUpload,
            togglePlay,
            formatDate,
            formatSizeKb,
            hasKnownMediaSizeKb,
            isLibraryAudioReady,
            closeOutline,
            musicalNote,
            playOutline,
            pauseOutline,
            cloudUploadOutline,
        };
    },
});
</script>

<style scoped>
.audio-library-picker {
    position: relative;
}

.file-label {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 0;
}

.audio-name {
    flex: 1;
    font-weight: 500;
}

.audio-size {
    font-size: 0.8em;
    color: var(--ion-color-medium);
}

.icon-container {
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
}

.custom-button {
    margin-bottom: 20px;
}

.library-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.library-modal {
    background: var(--ion-background-color, #fff);
    border-radius: 12px;
    width: min(760px, 95vw);
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.library-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--ion-color-light-shade, #e0e0e0);
}

.library-header h3 {
    margin: 0;
    font-size: 1.1em;
    font-weight: 600;
}

.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    font-size: 1.3em;
    color: var(--ion-color-medium);
}

.library-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* Search + sort row */
.library-controls {
    display: flex;
    gap: 8px;
}

.search-input {
    flex: 1;
    padding: 7px 12px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    border-radius: 6px;
    font-size: 0.9em;
    background: var(--ion-item-background, #fff);
    color: var(--ion-text-color, #000);
    outline: none;
}

.search-input:focus {
    border-color: var(--ion-color-primary, #3880ff);
}

.sort-select {
    padding: 7px 10px;
    border: 1px solid var(--ion-color-light-shade, #d7d8da);
    border-radius: 6px;
    font-size: 0.85em;
    background: var(--ion-item-background, #fff);
    color: var(--ion-text-color, #000);
    cursor: pointer;
    outline: none;
}

.sort-select:focus {
    border-color: var(--ion-color-primary, #3880ff);
}

.library-loading,
.library-empty {
    text-align: center;
    padding: 32px;
    color: var(--ion-color-medium);
}

.audio-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.audio-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;
    background: var(--ion-color-light, #f4f5f8);
}

.audio-item:hover {
    background: var(--ion-color-light-shade, #e2e3e8);
}

.audio-item--selected {
    background: var(--ion-color-primary-tint, #e0e8ff);
}

.audio-item--selected:hover {
    background: var(--ion-color-primary-tint, #d4ddff);
}

.audio-item-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
}

.audio-item-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.audio-item-date,
.audio-item-size {
    font-size: 0.8em;
    color: var(--ion-color-medium);
}

.audio-item-status {
    font-size: 0.8em;
}

.audio-item-status--pending {
    color: var(--ion-color-warning, #ffc409);
}

.audio-item-status--error {
    color: var(--ion-color-danger, #eb445a);
}

.audio-item-actions {
    margin-left: 12px;
    flex-shrink: 0;
    width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.play-btn {
    background: var(--ion-color-primary, #3880ff);
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 1.1em;
    transition: background 0.15s;
}

.play-btn:hover {
    background: var(--ion-color-primary-shade, #3171e0);
}

.play-btn--playing {
    background: var(--ion-color-danger, #eb445a);
}

.play-btn--playing:hover {
    background: var(--ion-color-danger-shade, #cf3c4f);
}

.processing-spinner {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--ion-color-medium);
}

/* Upload section — subtle, at the bottom */
.upload-section {
    margin-top: 4px;
    padding-top: 12px;
    border-top: 1px solid var(--ion-color-light-shade, #e0e0e0);
}

.upload-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px dashed var(--ion-color-medium, #92949c);
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88em;
    color: var(--ion-color-medium, #92949c);
    transition: border-color 0.15s, color 0.15s;
}

.upload-label:hover {
    border-color: var(--ion-color-primary, #3880ff);
    color: var(--ion-color-primary, #3880ff);
}

.upload-label input[type="file"] {
    display: none;
}
</style>
